import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import SEO from "@/components/SEO";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import CollectionsSection from "@/components/CollectionsSection";
import CategoryProductRows from "@/components/CategoryProductRows";
import GoogleAdUnit from "@/components/GoogleAdUnit";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Button } from "@/components/ui/button";
import BuyerNotificationBell from "@/components/BuyerNotificationBell";
import LanguageSelector from "@/components/LanguageSelector";
import { supabase } from "@/integrations/supabase/client";
import { Package, Settings, LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import CartIcon from "@/components/CartIcon";
import logo from "@/assets/logo.png";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Lazy load below-the-fold components
const PersonalizedSection = lazy(() => import("@/components/PersonalizedSection"));
const MoreCollections = lazy(() => import("@/components/MoreCollections"));
const CRBrandsSection = lazy(() => import("@/components/CRBrandsSection"));
const ArtisanSpotlight = lazy(() => import("@/components/ArtisanSpotlight"));
const FeaturedStoreStory = lazy(() => import("@/components/FeaturedStoreStory"));
const AdBanner = lazy(() => import("@/components/AdBanner"));
const Footer = lazy(() => import("@/components/Footer"));
const HomeInquiryDialog = lazy(() => import("@/components/HomeInquiryDialog"));

const Index = () => {
  const { user, signOut } = useAuth();
  const { isUSVisitor, t } = useLocale();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  // Defer below-the-fold sections to prioritize initial paint
  const [showMoreSections, setShowMoreSections] = useState(false);
  // Defer Footer even more since it's at the very bottom
  const [showFooter, setShowFooter] = useState(false);
  
  useEffect(() => {
    // Delay loading of below-the-fold content
    const timer = setTimeout(() => setShowMoreSections(true), 100);
    return () => clearTimeout(timer);
  }, []);
  
  useEffect(() => {
    // Delay Footer rendering by 3 seconds
    const timer = setTimeout(() => setShowFooter(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  
  // Defer notifications on mobile to reduce initial load
  const [showNotifications, setShowNotifications] = useState(!isMobile);
  
  useEffect(() => {
    if (isMobile && user) {
      const timer = setTimeout(() => setShowNotifications(true), 2000);
      return () => clearTimeout(timer);
    } else if (!isMobile) {
      setShowNotifications(true);
    }
  }, [isMobile, user]);

  // Use React Query with cache - same key as MobileBottomNav for shared cache
  const { data: isSeller = false } = useQuery({
    queryKey: ['seller-status', user?.id],
    queryFn: async () => {
      if (!user) return false;
      const { data } = await supabase
        .from("seller_profiles")
        .select("status")
        .eq("user_id", user.id)
        .maybeSingle();
      return data?.status === "approved";
    },
    enabled: !!user,
    staleTime: 1000 * 60 * 10, // 10 min cache - shared with MobileBottomNav
  });

  // Simple display name from email - no extra DB query needed
  const displayName = user?.email?.split("@")[0] || "Usuario";

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEO 
        url="https://www.ticomarket.com"
        description="Feria en línea de Costa Rica: artesanías, cerámica, joyería, cosmética natural, decoración y regalos hechos a mano por emprendedores ticos. Envíos a todo el país y pago con SINPE Móvil."
        keywords="artesanías Costa Rica, hecho a mano, productos ticos, cerámica artesanal, joyería tica, regalos costarricenses, cosmética natural, emprendedores ticos, SINPE Móvil"
      />

      
      {/* Mobile top header - Clean & Compact */}
      <header className="lg:hidden sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between h-14 px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="TicoMarket - Feria de emprendedores" className="h-8 w-auto" />
          </Link>

          {/* Auth buttons - Simplified */}
          <div className="flex items-center gap-1.5">
            <LanguageSelector />
            {user ? (
              <>
                {showNotifications && <BuyerNotificationBell />}
                <CartIcon />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                      <Avatar className="h-7 w-7">
                        <AvatarFallback className="text-xs bg-primary/10 text-primary">
                          {displayName?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate('/mis-pedidos')}>
                      <Package className="h-4 w-4 mr-2" />
                      {t('nav.myOrders')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/configuracion')}>
                      <Settings className="h-4 w-4 mr-2" />
                      {t('nav.settings')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                      <LogOut className="h-4 w-4 mr-2" />
                      {t('nav.signOut')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/iniciar-sesion">{t('nav.login')}</Link>
                </Button>
                {!isUSVisitor && (
                  <Button variant="tropical" size="sm" asChild>
                    <Link to="/registro-vendedor">{t('nav.sell')}</Link>
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Desktop Navigation - only render on desktop to avoid mobile query overhead */}
      {!isMobile && <Navigation />}
      
      <main>
        <HeroSection />
        <CollectionsSection />
        <Suspense fallback={null}><CRBrandsSection /></Suspense>
        <Suspense fallback={null}><AdBanner placement="mid_feed" /></Suspense>
        <CategoryProductRows />

        {/* Coffee */}
        <GoogleAdUnit slot="7371142955" className="container mx-auto px-4 my-8" />


        
        {/* Deferred sections - load after initial paint */}
        {showMoreSections && (
          <Suspense fallback={<div className="min-h-[200px]" />}>
            <PersonalizedSection />
            <MoreCollections />
            <FeaturedStoreStory />
            <ArtisanSpotlight />
            <AdBanner placement="pre_footer" />
          </Suspense>
        )}
      </main>
      {showMoreSections && (
        <Suspense fallback={null}>
          <HomeInquiryDialog />
        </Suspense>
      )}
      {showFooter && (
        <Suspense fallback={<div className="h-64 bg-foreground" />}>
          <Footer />
        </Suspense>
      )}
    </div>
  );
};

export default Index;
